import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { contentRegion, focusFirstAlive } from '../a11y/focusFirstAlive'
import { useBreakpoint } from '../responsive/useBreakpoint'
import { useFocusTrap } from './useFocusTrap'
import './overlay.css'

type Props = {
  open: boolean
  onClose: () => void
  /** The accessible name. A string, not an id — see below. */
  label: string
  children: ReactNode
  role?: 'dialog' | 'alertdialog'
  /**
   * Where the panel sits. `right` is the notifications drawer's full-height
   * sheet; `center` is both dialog kinds.
   *
   * A prop rather than a class the caller passes, and deliberately unlike the
   * `modal` flag this component declined: that one had a single value across
   * every caller, whereas this one genuinely has two. The alternative was
   * `notifications.css` writing a rule for `.overlay__panel`, which puts a
   * component's skin in another module's stylesheet — the shape
   * `controlTokens.test.ts` bans for controls and which is no better here.
   *
   * `right` has no separate mobile form. Its panel is `min(420px, 100%)`, so
   * it is full-bleed below 420px by arithmetic rather than by a breakpoint —
   * which is what `single-source.test.ts` requires, since a stylesheet may not
   * declare a layout media query at all.
   *
   * ## `sheet`
   *
   * `'center'` everywhere except a phone, where it becomes full-bleed —
   * M12-R19, and opt-in precisely so no existing caller moves.
   *
   * **Why this one needs `useBreakpoint()` when `'right'` does not.** The
   * drawer settles its own full-bleed by arithmetic (`min(420px, 100%)`), and
   * its docblock is right that a JS breakpoint would be a second answer to a
   * question CSS can already settle. That argument holds because width is the
   * only variable there. A sheet also has to lose its height cap, its radius
   * and the shell's 16px inset below the breakpoint, and no `min()` expresses
   * those — while `single-source.test.ts` forbids a stylesheet from declaring
   * the layout media query that would. So the switch is made where this repo
   * keeps every other layout switch: `useBreakpoint()`.
   *
   * ## `bottom`
   *
   * M12-R20 — `RoleMenu`'s phone form, a picker anchored to the bottom edge
   * rather than `sheet`'s full-bleed replacement for a whole form. Unlike
   * `sheet`, this is not a breakpoint switch on an existing placement: the
   * caller (`RoleMenu`) decides whether to mount `Overlay` at all, keeping its
   * own hand-rolled anchored dropdown for `tablet`/`desktop`/`wide` and
   * reaching for this placement only once `useBreakpoint()` itself says
   * `'phone'` — the same shape `'right'`'s drawer already has (always its own
   * geometry whenever mounted, no internal breakpoint check). So this value
   * needs no `useBreakpoint()` call of its own.
   */
  placement?: 'center' | 'right' | 'sheet' | 'bottom'
  /**
   * The panel's material. **Opaque by default, and that default is
   * load-bearing** — see the docblock on `.overlay__panel` in `overlay.css`:
   * a translucent panel lets whatever is beneath it into the composite, and
   * `--neg-soft` content over `/overview`'s lime hero measured 1.47:1 in dark.
   *
   * `'glass'` is opt-in for a panel that carries NO soft-fill content — the
   * new-category dialog, which is fields and buttons. The design system does
   * list `--glass-2` for "sheet", so this is the sanctioned material for the
   * case where it is safe; it is not safe for `ConfirmDialog`, whose whole job
   * is a destructive `--neg-soft` control, and those stay opaque.
   */
  surface?: 'opaque' | 'glass'
  /**
   * How wide a `center` panel may grow. `'default'` is the 440px cap every
   * dialog has; `'wide'` is 880px, for a dialog whose content grows a second
   * column in place — the category form's icon pane (#455), which opens BESIDE
   * the form inside this same panel rather than as a second overlay with a
   * second focus trap.
   *
   * A prop rather than a width the caller's stylesheet sets on
   * `.overlay__panel`, for the reason `placement` gives: that would put this
   * component's skin in another module's sheet. And a value that changes on a
   * mounted instance — the form widens when the pane opens and narrows when it
   * closes — so it has two live values rather than one fixed per caller.
   *
   * Both caps are `min(…, 100%)` of the overlay's padded box, so neither can
   * push the panel past the viewport. Width is the only variable, so no
   * breakpoint is read here; the caller decides whether to ask for `'wide'`
   * at all (the category form asks only at ≥ 640px, through
   * `useBreakpoint()`).
   */
  size?: 'default' | 'wide'
  /**
   * Where focus goes when the element that opened this is gone by the time it
   * closes. Not hypothetical: `ConfirmDialog` is opened by a Remove button
   * whose success path unmounts that button, and `.focus()` on a detached node
   * is a silent no-op that drops focus to `<body>`. Point it at a heading.
   * Optional: with nothing named, the content region catches it (#605).
   */
  restoreFocusTo?: RefObject<HTMLElement | null>
}

/**
 * Every mounted host, open or not, and every host of an OPEN overlay.
 *
 * Module-level because the inert sweep is a property of the whole stack, not of
 * one instance. The first version tracked neither and had two defects that only
 * appear with two overlays up — the shape Task 3's dialog over Task 6's drawer
 * takes, and one jsdom cannot police because it implements `inert` as markup
 * only:
 *
 *   - Every host is appended on MOUNT, open or not. So an open overlay inerted
 *     a second, still-closed host; when that one opened, its own sweep skipped
 *     it (already inert) and never released it. Its panel sat inside an inert
 *     subtree — unreachable by pointer and keyboard, with every assertion
 *     about it still passing.
 *   - Closing the LOWER overlay released everything it had inerted, while the
 *     higher one was still open. The app went tab-reachable behind a modal.
 *
 * Both are the same missing idea: inert is owned by the stack.
 */
const HOSTS = new Set<HTMLElement>()
/** Open hosts in the order they opened — the LAST is the top of the stack. */
let OPEN: HTMLElement[] = []
const closeHost = (host: HTMLElement) => {
  OPEN = OPEN.filter((h) => h !== host)
}
/** Only ever un-inert what we inerted; something may carry it independently. */
const INERTED = new Set<Element>()

function syncInert() {
  if (OPEN.length === 0) {
    for (const el of INERTED) el.removeAttribute('inert')
    INERTED.clear()
    return
  }
  const top = OPEN[OPEN.length - 1]
  // Everything that should be inert now: the page, plus every open host below
  // the top one (#506 — a confirm over the rule builder left the builder in the
  // accessibility tree). Closed hosts stay interactive-by-default; they hold
  // nothing.
  const want = new Set<Element>()
  for (const el of document.body.children) {
    if (el === top) continue
    if (HOSTS.has(el as HTMLElement)) {
      if (OPEN.includes(el as HTMLElement)) want.add(el)
      continue
    }
    // Task 2's toast host claims this. `inert` removes a subtree from the
    // accessibility tree, so a live region inside it is never announced —
    // which would silently break every toast raised while a dialog is open,
    // including the failure a ConfirmDialog deliberately stays open to show.
    if (el.hasAttribute('data-overlay-passthrough')) continue
    want.add(el)
  }
  // Release what is no longer wanted — the host that just became the top.
  for (const el of INERTED) {
    if (!want.has(el)) {
      el.removeAttribute('inert')
      INERTED.delete(el)
    }
  }
  for (const el of want) {
    if (el.hasAttribute('inert')) continue
    el.setAttribute('inert', '')
    INERTED.add(el)
  }
}

/**
 * The shared modal shell: portal, scrim, focus trap, Escape, focus restore,
 * inert background. Three consumers need all six, so they exist once.
 *
 * **`label`, not `labelledBy`.** An id-based API pushes id generation onto
 * every caller and fails silently when one forgets — the dialog simply has no
 * accessible name, which no test written against the caller would notice. It
 * lands on `aria-label` rather than an off-screen `<span>`: `aria-labelledby`
 * earns its machinery only when it points at a *visible* element, and a third
 * copy of the sr-only shape is the duplication `shell.css` already warns about.
 *
 * **No `modal` prop.** All three consumers want a trap and an inert background.
 * A flag with one value across every caller is an option nobody exercises.
 *
 * **Restoring focus is not `useFocusRequest`.** That hook moves focus TO a
 * named ref after an async step; this restores it to whatever held focus
 * before, which cannot be known in advance. What it *does* borrow is that
 * hook's hard-won lesson — the target may be gone — hence `restoreFocusTo`.
 */
export function Overlay({
  open,
  onClose,
  label,
  children,
  role = 'dialog',
  placement = 'center',
  surface = 'opaque',
  size = 'default',
  restoreFocusTo,
}: Props) {
  // `placement` is the INTENT; `sheet` is what that intent resolves to at this
  // viewport. Kept separate so the phone form is one predicate rather than
  // three repetitions of the same comparison down in the class lists.
  // The hook is called UNCONDITIONALLY and the comparison happens after.
  // `placement === 'sheet' && useBreakpoint() === 'phone'` reads fine and is a
  // conditional hook call: `&&` short-circuits, so a `center` overlay would
  // never call it, and the call order would change the moment a caller flipped
  // `placement` on a mounted instance.
  const breakpoint = useBreakpoint()
  const sheet = placement === 'sheet' && breakpoint === 'phone'

  const panel = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<HTMLElement | null>(null)
  const portal = useRef<HTMLDivElement | null>(null)
  portal.current ??= document.createElement('div')
  const leftFromTop = useRef(false)

  useEffect(() => {
    const host = portal.current
    if (host === null) return
    host.setAttribute('data-overlay-host', '')
    HOSTS.add(host)
    // `appendChild` on a node that is already the last child still detaches and
    // re-attaches it. The layout effect below has usually placed an opening
    // overlay's host there already, so move only when it is not (#506).
    if (document.body.lastElementChild !== host) document.body.appendChild(host)
    return () => {
      leftFromTop.current = OPEN[OPEN.length - 1] === host
      HOSTS.delete(host)
      closeHost(host)
      host.remove()
      syncInert()
    }
  }, [])

  /**
   * Captured BEFORE `useFocusTrap` is declared, and the order is the point —
   * effects run in declaration order, so a capture placed after the trap
   * records the panel's own first button and focus "returns" to a node about to
   * be unmounted. The first draft had it that way round.
   *
   * The cleanup runs on close AND unmount: a route change tears this subtree
   * down without `open` ever becoming false, and a restore keyed on the close
   * transition alone drops focus to `<body>`.
   */
  useEffect(() => {
    if (!open) return
    restoreTo.current = document.activeElement as HTMLElement | null
    return () => {
      // `focusFirstAlive` because `.focus()` on a detached node does nothing and
      // throws nothing — the silent failure this whole prop exists for. Shared
      // with the toast queue since #194: this was the first of three copies, and
      // the third had no last rung at all and landed a keyboard user on
      // `<body>`. It also adds the half every copy here missed — an element can
      // be attached and still refuse focus, so the move is confirmed.
      //
      // The chain: the trigger, the caller's named fallback, then the app's
      // content region (#605) — so an opener that unmounted lands on
      // `<main tabindex="-1">` rather than `<body>`, with no per-route ref.
      //
      // **The stack is left BEFORE the focus moves, and the order is the whole
      // fix — #215.** React runs every cleanup before it runs any effect body,
      // so while the release lived in the effect below, the restore always ran
      // one step too early: the trigger was still inside an `inert` subtree,
      // the browser refused `.focus()` silently, `focusFirstAlive` exhausted
      // its chain, and focus stayed on `<body>`. Measured in Chrome on every
      // close path — click, Escape, scrim, shortcut — while jsdom stayed green
      // through all of it, because jsdom implements `inert` as markup only and
      // focuses a node inside one happily.
      //
      // Releasing here rather than reordering the effects is what actually
      // works: no declaration order helps, since a cleanup can never run after
      // a body. The effect below still calls `syncInert()` on the same
      // transition; it is idempotent, and leaving it there keeps the OPEN
      // direction in one place.
      const host = portal.current
      // Only the TOP overlay gives focus back. One closing from beneath
      // (out of order, or a route teardown) must not drag focus out of the
      // overlay above it and into an inert subtree (#506).
      // On unmount the host effect's cleanup has already left the stack, and
      // recorded whether it did so from the top.
      const wasTop =
        host === null || (OPEN.includes(host) ? OPEN[OPEN.length - 1] === host : leftFromTop.current)
      if (host !== null) {
        closeHost(host)
        syncInert()
      }
      if (!wasTop) {
        restoreTo.current = null
        return
      }
      // The `exhaustive-deps` warning here is triaged, not fixed — issue 218,
      // which says so and which measured that it also fires on this file at
      // 815e103, before #217 touched it. It is about `restoreFocusTo?.current`
      // specifically, the CALLER's ref, and the rule's own remedy is the wrong
      // thing to do:
      //
      //   "copy it to a variable inside the effect and use that variable"
      //
      // A copy taken in the effect body freezes the value at OPEN time. The
      // whole contract of this prop is the opposite — the caller names a
      // fallback element, that element belongs to a subtree being torn down, and
      // what should be focused is whatever the ref points at NOW. A frozen copy
      // would restore focus to a node the caller has since replaced.
      //
      // The rule's concern — a stale ref to a react-rendered node — is exactly
      // what `focusFirstAlive` is built to survive: it treats a detached or
      // focus-refusing node as a failed rung and moves to the next, and it
      // CONFIRMS the move rather than assuming it. That is why a detached
      // fallback is safe here and a frozen one would not be.
      //
      // Suppressed at the single line rather than by switching the rule off:
      // three of the four findings the plugin reported were real and are fixed
      // in this commit.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      focusFirstAlive(restoreTo.current, restoreFocusTo?.current, contentRegion())
      restoreTo.current = null
    }
  }, [open, restoreFocusTo])

  useFocusTrap(panel, open)

  /**
   * Every overlay shares one z-index, so paint order is DOM order. Moving the
   * host to the end on open makes the top of the stack also the top of the
   * paint — an overlay mounted early and opened last would otherwise sit UNDER
   * the inert one below it.
   *
   * Caveat: moving a node resets the scroll position of anything scrollable
   * inside it. A child that sets a scroll offset during its FIRST render would
   * lose it here. Nothing does today; if something must, set it in an effect.
   *
   * A LAYOUT effect, deliberately: the host already holds the panel, and moving
   * a node that contains the focused element blurs it. Focus has not entered
   * the panel yet at this point (the trap runs in a passive effect), so nothing
   * is disturbed.
   */
  useLayoutEffect(() => {
    const host = portal.current
    if (!open || host === null) return
    if (document.body.lastElementChild !== host) document.body.appendChild(host)
  }, [open])

  useEffect(() => {
    const host = portal.current
    if (host === null) return
    if (open) {
      if (!OPEN.includes(host)) OPEN.push(host)
    } else closeHost(host)
    syncInert()
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className={
        [
          'overlay',
          placement === 'right' && 'overlay--right',
          sheet && 'overlay--sheet',
          placement === 'bottom' && 'overlay--bottom',
        ]
          .filter(Boolean)
          .join(' ')
      }
    >
      {/* Not a button and not focusable: the scrim is a click target for
          pointer users and nothing else. Escape is the keyboard path, and a
          focusable scrim would be a Tab stop that announces nothing. */}
      <div className="overlay__scrim" onClick={onClose} />
      <div
        ref={panel}
        className={
          [
            'overlay__panel',
            placement === 'right' && 'overlay__panel--right',
            sheet && 'overlay__panel--sheet',
            placement === 'bottom' && 'overlay__panel--bottom',
            surface === 'glass' && 'overlay__panel--glass',
            size === 'wide' && 'overlay__panel--wide',
            'enter',
          ]
            .filter(Boolean)
            .join(' ')
        }
        role={role}
        aria-modal="true"
        aria-label={label}
        /**
         * On the PANEL, not on `document`. Focus is trapped inside, so only the
         * topmost overlay ever sees the key — and with two open, that is the
         * difference between closing one and closing both. The first version
         * bound to `document` and called `stopPropagation()`, which stops
         * nothing: sibling listeners on the same node still run, and even
         * `stopImmediatePropagation` would have fired them in registration
         * order, closing the OUTER one first.
         */
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          event.stopPropagation()
          onClose()
        }}
      >
        {children}
      </div>
    </div>,
    portal.current,
  )
}
